import { useEffect, useRef, useState } from "react"
import { FormContainer, FormTopSection, FormInputsContainer, FormButtonsContainer } from "./ManageEWallet.styles"
import SectionLabel from "@components/SectionLabel/SectionLabel"
import Dropdown from "@components/Dropdown/Dropdown"
import TextInput from "@components/TextInput/TextInput"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import { useTranslation } from "react-i18next"
import type { SupportedEWallet } from "@hooks/useSupportedEWallet"
import { useUserRestrictions } from "@hooks/useUserRestrictions"

const FOCUS_SCROLL_DELAY = 500;
const SCROLL_OFFSET_ACCOUNT_NUMBER = 200;
const SCROLL_OFFSET_NICKNAME = 300;

export const EWalletForm = ({
    onSave,
    onRemove,
    supportedEWallets,
    initialName,
    initialAccountNumber,
    initialProvider,
    uuid,
    errorText,
    mode,
}: {
    onSave: (data: any) => void
    onRemove: (uuid: string) => void
    supportedEWallets?: SupportedEWallet[]
    initialName?: string
    initialAccountNumber?: string
    initialProvider?: string
    uuid?: string
    errorText?: string
    mode: 'add' | 'edit'
}) => {
    const { t } = useTranslation();
    const nameInputRef = useRef<HTMLInputElement>(null);
    const [name, setName] = useState(initialName || '')
    const [accountNumber, setAccountNumber] = useState(initialAccountNumber || '')
    const [selectedEWallet, setSelectedEWallet] = useState<any>(null)
    const formContainerRef = useRef<HTMLDivElement>(null)

    const { canManageEwalletAccounts } = useUserRestrictions()

    // Find the initial e-wallet from options if initialProvider is provided
    useEffect(() => {
        if (initialProvider) {
            const ewallet = supportedEWallets?.find(ewallet =>
                ewallet.provider === initialProvider ||
                ewallet.name === initialProvider
            );
            if (ewallet) setSelectedEWallet(ewallet);
        }
    }, [initialProvider]);

    const handleSaveClick = () => {
        onSave({
            phone_number: accountNumber,
            label: name,
            provider: selectedEWallet.provider,
            uuid,
        });
    };

    const handleRemoveClick = () => {
        onRemove(uuid!);
    };

    const handleAccountNumberFocus = () => {
        setTimeout(() => {
            if (formContainerRef.current) {
                const startPosition = formContainerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_ACCOUNT_NUMBER;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    formContainerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY)
    };

    const handleNicknameFocus = () => {
        setTimeout(() => {
            if (formContainerRef.current) {
                const startPosition = formContainerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_NICKNAME;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    formContainerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY)
    };

    return (
        <FormContainer>
            <FormTopSection ref={formContainerRef}>
                <SectionLabel>{t('bankAndAddress.ewallet.form.sectionLabel')}</SectionLabel>
                <FormInputsContainer>
                    <Dropdown
                        label={t('bankAndAddress.ewallet.form.ewalletSelector')}
                        placeholder={t('bankAndAddress.ewallet.form.ewalletPlaceholder')}
                        options={supportedEWallets || []}
                        value={selectedEWallet}
                        onChange={setSelectedEWallet}
                        usePortal={true}
                        disabled={!canManageEwalletAccounts()}
                    />
                    <TextInput
                        title={t('bankAndAddress.ewallet.form.phoneNumber')}
                        placeholder={t('bankAndAddress.ewallet.form.phoneNumberPlaceholder')}
                        value={accountNumber}
                        onChange={e => setAccountNumber(e.target.value.replace(/[^0-9]/g, ''))}
                        inputMode="tel"
                        onFocus={handleAccountNumberFocus}
                        errorText={errorText}
                        isError={!!errorText}
                        disabled={!canManageEwalletAccounts()}
                    />
                    <TextInput
                        title={t('bankAndAddress.ewallet.form.ewalletName')}
                        placeholder={t('bankAndAddress.ewallet.form.ewalletNamePlaceholder')}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        onFocus={handleNicknameFocus}
                        ref={nameInputRef}
                        disabled={!canManageEwalletAccounts()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Tab') {
                                e.preventDefault();
                                nameInputRef.current?.blur();
                            }
                        }}
                    />
                </FormInputsContainer>
            </FormTopSection>
            {canManageEwalletAccounts() && <FormButtonsContainer>
                {mode === 'edit' && <PrimaryButton onClick={handleRemoveClick} variant="danger">{t('bankAndAddress.ewallet.form.removeButton')}</PrimaryButton>}
                <PrimaryButton
                    onClick={handleSaveClick}
                    disabled={
                        !selectedEWallet ||
                        !accountNumber ||
                        !name
                    }
                >
                    {t('bankAndAddress.ewallet.form.saveButton')}
                </PrimaryButton>
            </FormButtonsContainer>}
        </FormContainer>
    )
}